import React from 'react';
import PropType from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import themeColors from '../../assets/styles/themeColors';
import Logo from '../../components/Logo/Logo';
import config from '../../config';
import Paragraph from '../../components/Paragraph/Paragraph';
import { variables } from '../../assets/styles/variables';
import useWindowSize from '../../util/hooks/useIsMobileHook';
import TestimonialImage from '../../assets/images/testimonial_alan.png';
import GoldCoins from '../../assets/images/gold-coins-1_1.png';
import SilverCoins from '../../assets/images/silver-coins-1_1.png';
import { ReactComponent as Quotes } from '../../assets/images/svg/quotes.svg';
import {
  mediaBelow,
  mediaBetween,
  mediaUp,
  pxToRem,
} from '../../assets/styles/helper';
import RegistrationPopup from './RegistrationPopup';
import GlobalStyle from '../../assets/styles/base';

const LeftPaneContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ isMobile }) => (isMobile ? '' : '8px')};
  overflow: clip;
  background: ${({ isMobile }) =>
    isMobile ? themeColors.colorSidebarBegin : 'none'};
  padding-bottom: ${({ isMobile }) => (isMobile ? '140px' : '0')};
  min-height: ${({ isMobile }) => (isMobile ? '100vh' : 'unset')};
  ${mediaBelow(variables.breakpoints.bpLgX)} {
    width: 100%;
  }
  ${mediaBetween(variables.breakpoints.bp1280, variables.breakpoints.bp1366)} {
    width: 534px;
  }
  ${mediaBetween(variables.breakpoints.bp1366, variables.breakpoints.bpXxxl)} {
    width: 534px;
  }
  ${mediaBetween(variables.breakpoints.bpXxxl, variables.breakpoints.bp1600)} {
    width: 612px;
  }
  ${mediaBetween(variables.breakpoints.bp1600, variables.breakpoints.bp1920)} {
    width: 750px;
  }
  ${mediaUp(variables.breakpoints.bp1920)} {
    width: 850px;
  }
`;

const FixedPopupWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${themeColors.colorWhite};
  padding: 0.5rem 1rem;
`;

const RegistrationSidebar = styled.div`
  background: ${themeColors.colorPrimary};
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: -2px;

  ${mediaBelow(variables.breakpoints.bpLg)} {
    padding: 16px;
    flex: 1;
  }

  svg {
    color: ${themeColors.colorHeaderLogoLight};
    height: 1.75rem;
    object-fit: contain;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  grid-gap: 16px;
`;

const RegistrationCardTypeOne = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${themeColors.colorRegCardBackground};
  padding: 1rem;
  border-radius: ${variables.borderRadius.borderRadiusMd};

  ${mediaBelow(variables.breakpoints.bpLg)} {
    height: 125px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 3rem 0 0rem 0;
  max-height: 358px;
`;

const RegistrationCardTypeTwo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  padding: 1rem;
  background: ${themeColors.colorRegCardBackground};
  border: 0.6px solid ${themeColors.colorCardTypeTwoBorder};
  border-radius: ${variables.borderRadius.borderRadiusMd};
  height: 214px;

  ${mediaBelow(variables.breakpoints.bpLg)} {
    height: auto;
    padding-bottom: 24px;
  }
`;

const MOBILE_IMAGE_WIDTH = '116px';
const MOBILE_IMAGE_HEIGHT = '124px';

const GOLD_COIN_SIZE_SM = '140px';
const GOLD_COIN_SIZE_MD = '170px';
const GOLD_COIN_SIZE_LG = '190px';

const SILVER_COIN_SIZE_SM = '115px';
const SILVER_COIN_SIZE_MD = '140px';
const SILVER_COIN_SIZE_LG = '155px';

const getGoldCoinSize = (width) => {
  if (width < variables.breakpoints.bpXl) return GOLD_COIN_SIZE_SM;
  if (width < variables.breakpoints.bp1366) return GOLD_COIN_SIZE_MD;
  return GOLD_COIN_SIZE_LG;
};

const getSilverCoinSize = (width) => {
  if (width < variables.breakpoints.bpXl) return SILVER_COIN_SIZE_SM;
  if (width < variables.breakpoints.bp1366) return SILVER_COIN_SIZE_MD;
  return SILVER_COIN_SIZE_LG;
};

const ImageContainer = styled.img`
  flex-shrink: 0;
  height: ${({ height }) => height || '166px'};
  width: ${({ width }) => width || '166px'};
  object-fit: contain;
  transition: opacity 0.2s ease;

  ${({ hidden }) =>
    hidden &&
    `
    opacity: 0;
    pointer-events: none;
  `}
`;

const QuotesContainer = styled.div`
  height: ${({ height }) => pxToRem(height) || pxToRem(34)};
  width: ${({ width }) => (width ? pxToRem(width) : pxToRem(34))};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? pxToRem(marginBottom) : '0'};
`;

const TestimonialTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  height: ${({ isMobile }) => (isMobile ? MOBILE_IMAGE_HEIGHT : '166px')};
  flex: 1;
  min-width: 0;
`;

const TestimonialParagraph = styled(Paragraph)`
  width: 100%;
  max-width: none;
  white-space: normal;
`;

const TestimonalTextAndLink = styled.div`
  display: block;
  width: 100%;
  justify-content: center;
  align-items: start;
`;
/* Re-instate if required in future 
const ViewMoreLink = styled.span`
  text-decoration: underline;
  color: ${themeColors.colorExpandLink};
  font-size: ${pxToRem(14)};
  margin-left: 8px;
`; */
const CoinsContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0;
  border-radius: ${({ isMobile }) => (isMobile ? '' : '0 0 8px 8px')};
  background: ${themeColors.colorSidebarBegin};
`;

const getTitleFontSize = (width, smallFont, mediumFont, largeFont) => {
  if (width < variables.breakpoints.bpLg) return smallFont;
  if (width <= variables.breakpoints.bpXxxl) return mediumFont;
  return largeFont;
};

const RegistrationSidebarWithPrompt = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < variables.breakpoints.bpLg;
  const isBelowXl = windowSize.width < variables.breakpoints.bpXl;
  // const [showFullTestimonial, setShowFullTestimonial] = useState(false);

  // const onClickViewMore = () => {
  //   // setShowFullTestimonial(true);
  // };
  return (
    <>
      <GlobalStyle />
      <LeftPaneContainer isMobile={isMobile}>
        <RegistrationSidebar isGS={config?.brokerTheme === 'GoldSilver'}>
          <Logo />
          <TitleContainer>
            <Paragraph
              fontWeight={1000}
              fontSize={getTitleFontSize(windowSize.width, 26, 28, 44)}
              color={themeColors.colorWhite}
              usePx
            >
              {isMobile
                ? t('quickRegistration.twoStep.leftPane.altTitle')
                : t('quickRegistration.twoStep.leftPane.title')}
            </Paragraph>

            <Paragraph
              bold
              fontSize={getTitleFontSize(windowSize.width, 20, 28, 36)}
              color={themeColors.colorRegCardSubtitleText}
              usePx
            >
              {isMobile
                ? t('quickRegistration.twoStep.leftPane.altSubtitle')
                : t('quickRegistration.twoStep.leftPane.subtitle')}
            </Paragraph>
          </TitleContainer>
          <CardsContainer isMobile={isMobile}>
            <RegistrationCardTypeOne>
              <Paragraph
                fontWeight={1000}
                fontSize={getTitleFontSize(windowSize.width, 34, 28, 40)}
                color={themeColors.colorWhite}
                usePx
              >
                {t('quickRegistration.twoStep.leftPane.cardOneTitle')}
              </Paragraph>
              <Paragraph
                bold
                color={themeColors.colorRegCardSubtitleText}
                fontSize={getTitleFontSize(windowSize.width, 18, 16, 20)}
                centerText
                usePx
              >
                {t('quickRegistration.twoStep.leftPane.cardOneSubtitle')}
              </Paragraph>
            </RegistrationCardTypeOne>
            <RegistrationCardTypeOne>
              <Paragraph
                fontWeight={1000}
                fontSize={getTitleFontSize(windowSize.width, 34, 28, 40)}
                color={themeColors.colorWhite}
                usePx
              >
                {t('quickRegistration.twoStep.leftPane.cardTwoTitle')}
              </Paragraph>
              <Paragraph
                bold
                color={themeColors.colorRegCardSubtitleText}
                fontSize={getTitleFontSize(windowSize.width, 18, 16, 20)}
                centerText
                usePx
              >
                {t('quickRegistration.twoStep.leftPane.cardTwoSubtitle')}
              </Paragraph>
            </RegistrationCardTypeOne>
          </CardsContainer>
          <RegistrationCardTypeTwo>
            <ImageContainer
              src={TestimonialImage}
              alt="human-image"
              marginRight="1rem"
              height={isMobile ? MOBILE_IMAGE_HEIGHT : ''}
              width={isBelowXl ? MOBILE_IMAGE_WIDTH : ''}
            />
            <TestimonialTextContainer isMobile={isMobile}>
              <div>
                <QuotesContainer
                  height={isMobile ? 24 : 34}
                  marginBottom={isMobile ? 12 : 0}
                >
                  <Quotes />
                </QuotesContainer>
                <TestimonalTextAndLink>
                  <TestimonialParagraph
                    color={themeColors.colorWhite}
                    fontSize={getTitleFontSize(windowSize.width, 14, 16, 20)}
                    fontWeight={500}
                    lineHeight={isMobile ? '1.5rem' : ''}
                    marginBottom={8}
                    usePx
                  >
                    {t('quickRegistration.twoStep.leftPane.testimonialPartial')}
                  </TestimonialParagraph>
                </TestimonalTextAndLink>
              </div>
              <Paragraph
                bold
                color={themeColors.colorCardTypeTwoBorder}
                fontSize={isMobile ? 16 : 18}
                usePx
              >
                {t('quickRegistration.twoStep.leftPane.testimonialProvider')}
              </Paragraph>
            </TestimonialTextContainer>
          </RegistrationCardTypeTwo>
          <Paragraph
            bold
            color={
              isMobile
                ? themeColors.colorCardTypeTwoBorder
                : themeColors.colorWhite
            }
            fontSize={16}
            usePx
          >
            {t('quickRegistration.twoStep.leftPane.footnote')}
          </Paragraph>
        </RegistrationSidebar>

        <CoinsContainer>
          {!isMobile && (
            <>
              <ImageContainer
                src={GoldCoins}
                alt="gold-coins"
                height={getGoldCoinSize(windowSize.width)}
                width={getGoldCoinSize(windowSize.width)}
                hidden={isMobile && isOpen}
                borderRadius="0 0 0 8px"
              />

              <ImageContainer
                src={SilverCoins}
                alt="silver-coins"
                height={getSilverCoinSize(windowSize.width)}
                width={getSilverCoinSize(windowSize.width)}
                hidden={isMobile && isOpen}
                borderRadius="0 0 8px 0"
              />
            </>
          )}
        </CoinsContainer>
        {isMobile && isOpen && (
          <FixedPopupWrapper>
            {' '}
            <RegistrationPopup isOpen={isOpen} setIsOpen={setIsOpen} />{' '}
          </FixedPopupWrapper>
        )}
      </LeftPaneContainer>
    </>
  );
};

RegistrationSidebarWithPrompt.propTypes = {
  isOpen: PropType.bool,
  setIsOpen: PropType.func,
};

export default RegistrationSidebarWithPrompt;
